import { useEffect } from "react";
import { Link } from "react-router-dom";
import { FiPhone, FiMail, FiGlobe, FiMapPin, FiExternalLink } from "react-icons/fi";
import MarketingPageLayout from "../../components/marketing/MarketingPageLayout";
import { BRAND_NAME, COMPANY_LEGAL_NAME } from "../../constants/brand";
import {
  CONTACT_EMAIL,
  CONTACT_PHONES,
  OFFICE_ADDRESS,
  SSDI_WEBSITE_LABEL,
  SSDI_WEBSITE_URL,
  SUPPORT_MAILTO_HREF,
  openSupportMail,
} from "../../constants/contact";
import "../../styles/auth.css";

const TEAM = [
  { initials: "AK", name: "Mr Ashwani Kumar", role: "Founder" },
  { initials: "A", name: "Mrs Anju", role: "Co-founder" },
];

function ContactCard({ icon, title, href, onActivate, children }) {
  const inner = (
    <>
      <div className="marketing-card__icon" aria-hidden>
        {icon}
      </div>
      <h2 className="marketing-card__title">{title}</h2>
      <div className="contact-info-card__body">{children}</div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        onClick={onActivate}
        className="marketing-card contact-info-card contact-info-card--link"
      >
        {inner}
      </a>
    );
  }

  return <article className="marketing-card contact-info-card">{inner}</article>;
}

export default function ContactPage() {
  useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute("content") || "";
    document.title = `Contact Us | ${BRAND_NAME}`;
    if (meta) {
      meta.setAttribute(
        "content",
        "Contact FastRecovery in Delhi. Call +91 8796814100 or email fastrecovery26@gmail.com for support and product assistance."
      );
    }
    return () => {
      document.title = previousTitle;
      if (meta) meta.setAttribute("content", previousDescription);
    };
  }, []);

  return (
    <MarketingPageLayout>
      <header className="marketing-page__hero contact-hero">
        <p className="contact-hero__eyebrow">FastRecovery support</p>
        <h1 className="marketing-page__title">Get in Touch</h1>
        <p className="marketing-page__lead">
          We’re here to help. Reach out to our team for support, queries, or any assistance.
        </p>
      </header>

      <section className="contact-info-grid" aria-label="Contact information">
        <ContactCard icon={<FiMapPin size={22} />} title="Office Address">
          <p>{OFFICE_ADDRESS}</p>
        </ContactCard>
        <ContactCard icon={<FiPhone size={22} />} title="Phone Numbers">
          <ul className="contact-link-list">
            {CONTACT_PHONES.map((phone) => (
              <li key={phone.tel}>
                <a href={`tel:${phone.tel}`}>{phone.label}</a>
              </li>
            ))}
          </ul>
        </ContactCard>
        <ContactCard
          icon={<FiMail size={22} />}
          title="Email"
          href={SUPPORT_MAILTO_HREF}
          onActivate={openSupportMail}
        >
          {CONTACT_EMAIL}
        </ContactCard>
        <ContactCard icon={<FiGlobe size={22} />} title="Website">
          <a href={SSDI_WEBSITE_URL} target="_blank" rel="noopener noreferrer">
            {SSDI_WEBSITE_LABEL}
            <FiExternalLink aria-hidden />
          </a>
        </ContactCard>
      </section>

      <section className="contact-team" aria-labelledby="contact-team-heading">
        <h2 id="contact-team-heading" className="contact-section-title">
          Meet Our Team
        </h2>
        <div className="contact-team-grid">
          {TEAM.map((member) => (
            <article key={member.name} className="marketing-card contact-team-card">
              <div className="contact-team-card__avatar" aria-hidden>
                {member.initials}
              </div>
              <h3 className="contact-team-card__name">{member.name}</h3>
              <p className="contact-team-card__role">{member.role}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="marketing-card contact-developer" aria-label="Developer">
        <p className="contact-developer__kicker">Developed By</p>
        <h2 className="marketing-card__title">{COMPANY_LEGAL_NAME}</h2>
        <a href={SSDI_WEBSITE_URL} target="_blank" rel="noopener noreferrer">
          {SSDI_WEBSITE_LABEL}
          <FiExternalLink aria-hidden />
        </a>
        <div className="contact-developer__office">
          <p className="contact-developer__office-label">Office Location</p>
          <p className="contact-developer__office-address">{OFFICE_ADDRESS}</p>
        </div>
      </section>

      <section className="marketing-callout contact-cta" aria-label="Need assistance">
        <p className="marketing-callout__title">Have a question or need assistance?</p>
        <p className="marketing-callout__text">Our team is ready to help you.</p>
        <div className="contact-cta__actions">
          <a className="primary-page-btn" href={`tel:${CONTACT_PHONES[0].tel}`}>
            <FiPhone aria-hidden />
            Call Us
          </a>
          <a className="secondary-page-btn" href={SUPPORT_MAILTO_HREF} onClick={openSupportMail}>
            <FiMail aria-hidden />
            Email Us
          </a>
        </div>
      </section>

      <div className="marketing-page__actions">
        <Link to="/" className="primary-page-btn">
          ← Back to Login
        </Link>
      </div>
    </MarketingPageLayout>
  );
}
